import { useState, useRef, useCallback, useEffect } from 'react';

const DEFAULT_DELAY = 3000;

/**
 * Auto-hides controls after inactivity.
 *
 * Returns `hidden` state plus `show`/`hideNow` helpers.
 * The consumer is responsible for placing a mouse-capture overlay in JSX
 * (needed because the video player is an iframe that swallows mouse events).
 */
export function useAutoHideControls(options?: {
  delay?: number;
  enabled?: boolean;
}) {
  const delay = options?.delay ?? DEFAULT_DELAY;
  const enabled = options?.enabled ?? true;
  const [hidden, setHidden] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = useCallback(() => {
    setHidden(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHidden(true), delay);
  }, [delay]);

  const hideNow = useCallback(() => {
    clearTimeout(timerRef.current);
    setHidden(true);
  }, []);

  // Reset when enabled/disabled
  useEffect(() => {
    if (enabled) {
      show(); // controls visible + start 3s timer
    } else {
      clearTimeout(timerRef.current);
      setHidden(false); // always visible when disabled
    }
    return () => clearTimeout(timerRef.current);
  }, [enabled, show]);

  return { hidden, show, hideNow };
}
